/**
 * validation.ts — DemoState integrity validation.
 *
 * validateDemoState() checks referential integrity and returns a list of errors.
 * An empty array means the state is valid.
 */

import type { DemoState } from './schema';

export interface ValidationError {
  field: string;
  message: string;
}

export function validateDemoState(state: DemoState): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!state || typeof state !== 'object') {
    return [{ field: 'root', message: 'State is not an object' }];
  }

  // ─── Version ────────────────────────────────────────────────────────
  if (typeof state.version !== 'number') {
    errors.push({ field: 'version', message: 'Missing or invalid version' });
  }

  // ─── Unique IDs ──────────────────────────────────────────────────────
  function checkUniqueIds(items: { id: string }[], name: string) {
    const ids = items.map((i) => i.id);
    const dupes = ids.filter((id, idx) => ids.indexOf(id) !== idx);
    if (dupes.length > 0) {
      errors.push({ field: name, message: `Duplicate IDs: ${dupes.join(', ')}` });
    }
  }

  if (Array.isArray(state.userProfiles)) checkUniqueIds(state.userProfiles, 'userProfiles');
  if (Array.isArray(state.workspaces)) checkUniqueIds(state.workspaces, 'workspaces');
  if (Array.isArray(state.plenarySessions)) checkUniqueIds(state.plenarySessions, 'plenarySessions');
  if (Array.isArray(state.agendaItems)) checkUniqueIds(state.agendaItems, 'agendaItems');
  if (Array.isArray(state.documents)) checkUniqueIds(state.documents, 'documents');
  if (Array.isArray(state.issues)) checkUniqueIds(state.issues, 'issues');
  if (Array.isArray(state.drafts)) checkUniqueIds(state.drafts, 'drafts');
  if (Array.isArray(state.notifications)) checkUniqueIds(state.notifications, 'notifications');

  // ─── Workspace references ────────────────────────────────────────────
  const workspaceIds = new Set((state.workspaces ?? []).map((w) => w.id));
  const userIds = new Set((state.userProfiles ?? []).map((u) => u.id));

  if (state.activeWorkspaceId && !workspaceIds.has(state.activeWorkspaceId)) {
    errors.push({ field: 'activeWorkspaceId', message: `activeWorkspaceId '${state.activeWorkspaceId}' not found in workspaces` });
  }

  // ─── Membership references ───────────────────────────────────────────
  (state.organizationMemberships ?? []).forEach((m) => {
    if (!userIds.has(m.userId)) {
      errors.push({ field: 'organizationMemberships', message: `userId '${m.userId}' not found in userProfiles` });
    }
  });

  (state.workspaceMemberships ?? []).forEach((m) => {
    if (!userIds.has(m.userId)) {
      errors.push({ field: 'workspaceMemberships', message: `userId '${m.userId}' not found in userProfiles` });
    }
    if (!workspaceIds.has(m.workspaceId)) {
      errors.push({ field: 'workspaceMemberships', message: `workspaceId '${m.workspaceId}' not found in workspaces` });
    }
  });

  // ─── Agenda items reference valid sessions ───────────────────────────
  const sessionIds = new Set((state.plenarySessions ?? []).map((s) => s.id));
  (state.agendaItems ?? []).forEach((item) => {
    if (!sessionIds.has(item.plenarySessionId)) {
      errors.push({ field: 'agendaItems', message: `agendaItem '${item.id}' references unknown session '${item.plenarySessionId}'` });
    }
  });

  // ─── Issue lifecycle consistency ─────────────────────────────────────
  (state.issues ?? []).forEach((issue) => {
    if (issue.status === 'arxivat' && !issue.archivedAt) {
      errors.push({ field: 'issues', message: `issue '${issue.id}' has status 'arxivat' but archivedAt is null` });
    }
  });

  // ─── Preferences ─────────────────────────────────────────────────────
  if (!state.preferences || typeof state.preferences !== 'object') {
    errors.push({ field: 'preferences', message: 'Missing preferences object' });
  }

  return errors;
}

/**
 * Returns true if the state passes all critical validations.
 */
export function isDemoStateValid(state: DemoState): boolean {
  const errors = validateDemoState(state);
  // Filter out non-critical warnings (e.g. orphan agenda items from demo data)
  const critical = errors.filter((e) =>
    e.field === 'root' ||
    e.field === 'version' ||
    e.field === 'activeWorkspaceId' ||
    e.field === 'preferences'
  );
  return critical.length === 0;
}
